import { HierarcicalObjectReference, ObjectData, ObjectDB, ObjectId, SearchOptions } from "@novorender/data-js-api";
import { isResponseError } from "@procore/core-http";

interface SearchOptionsExt extends SearchOptions {
    skipPermissionCheck?: boolean;
}

/** Optional ID mapping added by Procore (or future legacy) ObjectDB implementations. */
type ObjectDBWithResolve = ObjectDB & {
    resolveObjectIdsToInstanceIds?(objectIds: ObjectId[], signal: AbortSignal | undefined): Promise<(string | null)[]>;
    resolveInstanceIdsToObjectIds?(
        instanceIds: string[],
        signal: AbortSignal | undefined,
    ): Promise<(ObjectId | null)[]>;
};

/**
 * Wrapper around ObjectDB with some additional features
 * - Caches metadata for requests by object ID
 * - Filters out objects that the user don't have permissions to by default
 *
 * Also it patches `loadCompleteMetadata` with caching
 */
export class AppObjectDB implements ObjectDB {
    private readonly metadataCache = new ObjectMetadataCache();
    /**
     * Whether the /resolve endpoint is available for this session.
     * undefined - unknown, true - available, false - unavailable
     */
    private _resolveEndpointAvailable: boolean | undefined = undefined;

    get isResolveEndpointAvailable() {
        return this._resolveEndpointAvailable;
    }

    constructor(
        private readonly db: ObjectDB,
        private readonly availableIds: Set<ObjectId> | undefined,
    ) {
        const patchedDb = db as ObjectDB & { loadCompleteMetadata: (id: number) => Promise<ObjectData> };
        const originalLoadCompleteMetadata = patchedDb.loadCompleteMetadata;
        patchedDb.loadCompleteMetadata = (id: number) => {
            return this.metadataCache.getOrLoadMetadata(id, () => originalLoadCompleteMetadata(id));
        };
    }

    async getObjectMetdata(id: number, skipPermissionCheck = false): Promise<ObjectData> {
        if (skipPermissionCheck || !this.availableIds || this.availableIds.has(id)) {
            return this.metadataCache.getOrLoadMetadata(id, () => this.db.getObjectMetdata(id));
        }

        throw new ObjectAccessForbiddenError(id);
    }

    async *search(
        { skipPermissionCheck, ...filter }: SearchOptionsExt,
        signal: AbortSignal | undefined,
    ): AsyncIterableIterator<HierarcicalObjectReference> {
        for await (const obj of this.db.search(filter, signal)) {
            if (skipPermissionCheck || !this.availableIds || this.availableIds.has(obj.id)) {
                yield obj;
            }
        }
    }

    async descendants(
        object: HierarcicalObjectReference,
        signal: AbortSignal | undefined,
        skipPermissionCheck = false,
    ): Promise<ObjectId[]> {
        if (skipPermissionCheck || !this.availableIds || this.availableIds.has(object.id)) {
            return this.db.descendants(object, signal);
        }

        throw new ObjectAccessForbiddenError(object.id);
    }

    /**
     * Batch-resolve object ids → Procore/InstanceId strings. Returns `undefined` if the endpoint
     * resolution index is not available for the scene; callers should fall back to search.
     */
    async resolveObjectIdsToInstanceIds(
        objectIds: ObjectId[],
        signal: AbortSignal | undefined,
    ): Promise<(string | null)[] | undefined> {
        if (this._resolveEndpointAvailable === false) {
            return undefined;
        }
        const inner = this.db as ObjectDBWithResolve;
        if (!inner.resolveObjectIdsToInstanceIds) {
            this._resolveEndpointAvailable = false;
            return undefined;
        }
        try {
            const data = await inner.resolveObjectIdsToInstanceIds(objectIds, signal);
            this._resolveEndpointAvailable = true;
            return data.map((value, i) => {
                const id = objectIds[i]!;
                if (this.availableIds && !this.availableIds.has(id)) {
                    return null;
                }
                return value;
            });
        } catch (e) {
            if (isResponseError(e) && e.status === 404) {
                this._resolveEndpointAvailable = false;
                return undefined;
            }
            throw e;
        }
    }

    /**
     * Batch-resolve Procore/InstanceId strings → object ids. See {@link resolveObjectIdsToInstanceIds}.
     */
    async resolveInstanceIdsToObjectIds(
        instanceIds: string[],
        signal: AbortSignal | undefined,
    ): Promise<(ObjectId | null)[] | undefined> {
        if (this._resolveEndpointAvailable === false) {
            return undefined;
        }
        const inner = this.db as ObjectDBWithResolve;
        if (!inner.resolveInstanceIdsToObjectIds) {
            this._resolveEndpointAvailable = false;
            return undefined;
        }
        try {
            const data = await inner.resolveInstanceIdsToObjectIds(instanceIds, signal);
            this._resolveEndpointAvailable = true;
            return data.map((value) => {
                if (value === null) {
                    return null;
                }
                if (this.availableIds && !this.availableIds.has(value)) {
                    return null;
                }
                return value;
            });
        } catch (e) {
            if (isResponseError(e) && e.status === 404) {
                this._resolveEndpointAvailable = false;
                return undefined;
            }
            throw e;
        }
    }
}

export class ObjectAccessForbiddenError extends Error {
    constructor(public readonly objectId: ObjectId) {
        super(`Object ${objectId} is not available`);
    }
}

class ObjectMetadataCache {
    private cache = new Map<ObjectId, { promise: Promise<ObjectData>; timestamp: number }>();

    getOrLoadMetadata = async (objectId: ObjectId, load: (objectId: ObjectId) => Promise<ObjectData>) => {
        let cacheEntry = this.cache.get(objectId);

        if (!cacheEntry) {
            const promise = load(objectId);
            cacheEntry = { promise, timestamp: Date.now() };
            this.cache.set(objectId, cacheEntry);
            this.cleanCache();
        }

        return await cacheEntry.promise;
    };

    private cleanCache = () => {
        if (this.cache.size > 1000) {
            [...this.cache.entries()]
                .sort((a, b) => a[1].timestamp - b[1].timestamp)
                .slice(0, 200)
                .forEach(([key]) => this.cache.delete(key));
        }
    };
}
